import { useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Loader2 } from "lucide-react";

const it = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");

// Registrazione (o annullamento) della disdetta scritta: l'accordo resta valido fino al 31/12 e non si rinnova.
export const DisdettaDialog = ({ accordo, open, onOpenChange, onSaved }) => {
  const [f, setF] = useState({ data_disdetta: new Date().toISOString().slice(0, 10), inviata_da: "ente", motivo: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const d = accordo?.disdetta;

  const registra = async () => {
    setBusy(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    if (file) fd.append("file", file);
    try { await api.post(`/admin/accordi/${accordo.id}/disdetta`, fd); toast.success("Disdetta registrata: l'accordo non si rinnoverà"); onSaved(); onOpenChange(false); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const annulla = async () => {
    try { await api.post(`/admin/accordi/${accordo.id}/disdetta/annulla`); toast.success("Disdetta annullata: rinnovo automatico ripristinato"); onSaved(); onOpenChange(false); }
    catch (e) { toast.error(apiError(e)); }
  };
  const apriDoc = async () => {
    const { data } = await api.get(`/admin/accordi/${accordo.id}/disdetta/documento`, { responseType: "blob" });
    window.open(URL.createObjectURL(data), "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="disdetta-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-forest">Disdetta accordo {accordo?.codice}</DialogTitle>
          <DialogDescription>L'accordo resta valido fino al {it(accordo?.scadenza_corrente)} e poi non si rinnova. Il referente riceve un'email.</DialogDescription>
        </DialogHeader>
        {d ? (
          <div className="space-y-3 text-sm" data-testid="disdetta-registrata">
            <p>Disdetta del <b>{it(d.data)}</b> inviata {d.inviata_da === "ente" ? "dall'ente" : "da Assietica"}. Registrata da {d.registrata_da}.</p>
            {d.motivo && <p className="text-muted-foreground">Motivo: {d.motivo}</p>}
            {d.ha_documento && <Button size="sm" variant="outline" onClick={apriDoc} data-testid="disdetta-documento" className="gap-1"><FileText className="h-3.5 w-3.5" />Lettera di disdetta</Button>}
            <Button variant="outline" onClick={annulla} data-testid="disdetta-annulla" className="w-full">Annulla la disdetta (ripristina il rinnovo)</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Data della comunicazione</Label><Input type="date" value={f.data_disdetta} onChange={(e) => setF({ ...f, data_disdetta: e.target.value })} data-testid="disdetta-data" /></div>
              <div className="space-y-1"><Label className="text-xs">Inviata da</Label>
                <select value={f.inviata_da} onChange={(e) => setF({ ...f, inviata_da: e.target.value })} data-testid="disdetta-inviata-da" className="h-10 w-full rounded-md border px-2 text-sm"><option value="ente">L'ente convenzionato</option><option value="assietica">Assietica</option></select>
              </div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Motivo (facoltativo)</Label><Textarea value={f.motivo} onChange={(e) => setF({ ...f, motivo: e.target.value })} data-testid="disdetta-motivo" /></div>
            <div className="space-y-1"><Label className="text-xs">Lettera di disdetta (PDF, facoltativa)</Label><Input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="disdetta-file" /></div>
            <Button onClick={registra} disabled={busy || !f.data_disdetta} data-testid="disdetta-conferma" className="w-full bg-red-700 hover:bg-red-800">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Registra disdetta</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
